import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ManualLink from '../../components/ManualLink';
import { MentorshipRoster } from '../../components/Vouch/MentorshipRoster';
import { VouchingCreditsStrip } from '../../components/Vouch/VouchingCreditsStrip';
import { GET_USER_BY_USERNAME_QUERY } from '../../graphql/resolvers/userQueries/getUserByUsername.gql';
import { useMentorships } from '../../hooks/useMentorships';
import { useVouchingCredits } from '../../hooks/useVouchingCredits';
import { VouchConfirm } from './VouchConfirm';
import { VouchHowItWorks } from './VouchHowItWorks';
import { VouchLocked } from './VouchLocked';
import { VouchReceivedSection } from './VouchReceivedSection';
import { VouchSearch } from './VouchSearch';
import { VOUCH_COPY } from './vouch.copy';
import './VouchPage.scss';

export default function VouchPage() {
  const enabled = import.meta.env.VITE_ENABLE_VOUCHING === 'true';
  const [params, setParams] = useSearchParams();
  const [picked, setPicked] = useState<string | null>(null);
  const {
    available,
    committed,
    loading: creditsLoading,
    refetch: refetchCredits,
  } = useVouchingCredits();
  const { refetch: refetchMentors } = useMentorships();

  // deep-link ?user=<username> pre-selects
  useEffect(() => {
    const u = params.get('user');
    if (u) setPicked(u);
  }, [params]);

  const { data } = useQuery(GET_USER_BY_USERNAME_QUERY, {
    variables: { username: picked ?? '' },
    skip: !picked,
    fetchPolicy: 'cache-first',
  });
  const resolved = (
    data as { getUserByUsername?: { id: string; username: string } } | undefined
  )?.getUserByUsername;

  const clearSelection = () => {
    setPicked(null);
    if (params.get('user')) {
      params.delete('user');
      setParams(params, { replace: true });
    }
  };

  const onVouched = () => {
    refetchCredits();
    refetchMentors();
  };

  if (!enabled) return null;

  return (
    <section id="vouch-page" className="vouch-page">
      <div className="vouch-page__header">
        <h1 className="vouch-page__title">{VOUCH_COPY.pageTitle}</h1>
        <ManualLink section="vouching" label="How vouching works" />
      </div>
      <VouchingCreditsStrip />

      <div className="vouch-page__give">
        {!creditsLoading && available < 1 ? (
          <VouchLocked committed={committed} />
        ) : (
          <>
            <h2 className="vouch-page__heading">{VOUCH_COPY.searchHeading}</h2>
            {picked && resolved ? (
              <VouchConfirm
                target={{ userId: resolved.id, username: resolved.username }}
                available={available}
                onVouched={onVouched}
                onCancel={clearSelection}
              />
            ) : (
              <VouchSearch onPick={setPicked} />
            )}
          </>
        )}
      </div>

      {/* MentorshipRoster renders its own "Mentorships" heading per TRIL-382 —
          no page-level givenHeading wrapper to avoid a double heading. */}
      <div className="vouch-page__given">
        <MentorshipRoster />
      </div>

      <VouchReceivedSection />
      <VouchHowItWorks />
    </section>
  );
}
